import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  LogOut, RefreshCw, Search, Filter, Download,
  Trash2, ChevronDown, Users, Calendar, Clock, BarChart2,
  AlertCircle, CheckCircle2, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  "New":         "bg-blue-100 text-blue-700 border-blue-200",
  "In Progress": "bg-amber-100 text-amber-700 border-amber-200",
  "Closed":      "bg-green-100 text-green-700 border-green-200",
};

const STATUSES = ["New", "In Progress", "Closed"];
const PAGE_SIZE = 25;

const SOURCE_BADGES = {
  "Quote Request":    { label: "Quote Request",    cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  "Live Chat Widget": { label: "Live Chat",        cls: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  "Contact Form":     { label: "Contact Form",     cls: "bg-gray-100 text-gray-700 border-gray-200" },
  "Contact":          { label: "Contact Form",     cls: "bg-gray-100 text-gray-700 border-gray-200" }, // legacy
};
function sourceBadge(src) {
  return SOURCE_BADGES[src] || { label: src || "Contact", cls: "bg-gray-100 text-gray-700 border-gray-200" };
}

function authHeaders() {
  const token = localStorage.getItem("adminToken");
  return { Authorization: `Bearer ${token}` };
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-gray-200 p-5 flex items-center gap-4">
      <div className={`w-11 h-11 flex items-center justify-center ${accent}`}>
        <Icon size={20} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#111827]">{value ?? "—"}</p>
        <p className="text-xs text-[#6B7280] font-medium uppercase tracking-wide mt-0.5">{label}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [stats, setStats]           = useState(null);
  const [enquiries, setEnquiries]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]           = useState("");

  const [search, setSearch]         = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatus]   = useState("All");
  const [sourceFilter, setSource]   = useState("All");
  const [page, setPage]             = useState(0);
  const [totalRows, setTotalRows]   = useState(0);

  const [deleting, setDeleting]     = useState(null);
  const [updating, setUpdating]     = useState(null);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError("");
    try {
      const headers = authHeaders();
      const params = {
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      if (search) params.search = search;
      if (statusFilter !== "All") params.status = statusFilter;
      if (sourceFilter !== "All") params.source = sourceFilter;
      const [statsRes, enqRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/enquiries`, { headers, params }),
      ]);
      setStats(statsRes.data);
      setEnquiries(enqRes.data.items || []);
      setTotalRows(enqRes.data.total ?? (enqRes.data.items?.length || 0));
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin");
      } else {
        setError("Failed to load data. Please refresh.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate, page, search, statusFilter, sourceFilter]);

  useEffect(() => {
    if (!localStorage.getItem("adminToken")) { navigate("/admin"); return; }
    fetchAll();
  }, [fetchAll, navigate]);

  // Debounce the search input by 400ms and reset to page 0 on any filter change
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setPage(0); }, [statusFilter, sourceFilter]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin");
  };

  const handleStatusChange = async (enquiry, newStatus) => {
    setUpdating(enquiry.id);
    try {
      await axios.patch(
        `${API}/admin/enquiries/${enquiry.id}/status`,
        { status: newStatus },
        { headers: authHeaders() }
      );
      setEnquiries(prev =>
        prev.map(e => e.id === enquiry.id ? { ...e, status: newStatus } : e)
      );
    } catch {
      setError("Failed to update status.");
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, { headers: authHeaders() });
      setEnquiries(prev => prev.filter(e => e.id !== id));
    } catch {
      setError("Failed to delete enquiry.");
    } finally {
      setDeleting(null);
    }
  };

  // Rows are already server-side filtered & paginated; total is authoritative
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const pageStart  = totalRows === 0 ? 0 : page * PAGE_SIZE + 1;
  const pageEnd    = Math.min(totalRows, page * PAGE_SIZE + enquiries.length);

  const exportPDF = async () => {
    // Fetch full filtered set for export (up to backend hard cap of 500)
    let rows = enquiries;
    try {
      const headers = authHeaders();
      const params = { skip: 0, limit: 500 };
      if (search) params.search = search;
      if (statusFilter !== "All") params.status = statusFilter;
      if (sourceFilter !== "All") params.source = sourceFilter;
      const res = await axios.get(`${API}/admin/enquiries`, { headers, params });
      rows = res.data.items || rows;
    } catch (err) {
      console.warn("Export: failed to fetch full filtered set, falling back to current page.", err);
    }

    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(15, 98, 254);
    doc.text("JAIN INFOTECH — Enquiry Report", 14, 16);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleString()}   Total exported: ${rows.length}`, 14, 23);

    autoTable(doc, {
      startY: 28,
      head: [["#", "Name", "Company", "Phone", "Email", "Subject", "Source", "Status", "Date"]],
      body: rows.map((e, i) => [
        i + 1,
        e.name || "",
        e.company || "—",
        e.phone || "",
        e.email || "",
        e.subject || "—",
        sourceBadge(e.source).label,
        e.status || "New",
        e.submitted_at ? new Date(e.submitted_at).toLocaleDateString("en-IN") : "",
      ]),
      headStyles: { fillColor: [15, 98, 254], textColor: 255, fontStyle: "bold", fontSize: 8 },
      bodyStyles: { fontSize: 7.5, textColor: [30, 30, 30] },
      alternateRowStyles: { fillColor: [245, 248, 255] },
      margin: { left: 14, right: 14 },
      styles: { cellPadding: 2.5 },
    });

    doc.save(`jain-infotech-enquiries-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={36} className="animate-spin text-[#0F62FE] mx-auto" />
          <p className="text-[#6B7280] mt-3 text-sm">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6] font-sans">

      {/* Top Nav */}
      <header className="bg-[#2D3748] shadow-lg sticky top-0 z-30">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg width="38" height="32" viewBox="0 0 54 46" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="jgl2" gradientUnits="userSpaceOnUse" x1="16" y1="0" x2="36" y2="0">
                  <stop offset="50%" stopColor="#0F62FE"/>
                  <stop offset="50%" stopColor="#EF4444"/>
                </linearGradient>
              </defs>
              <path d="M3,23 A23,15 0 0,1 51,23" stroke="#EF4444" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
              <path d="M51,23 A23,15 0 0,1 3,23" stroke="#0F62FE" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
              <text x="16" y="34" fontSize="26" fontWeight="900" fontFamily="Arial Black,Arial,sans-serif" fill="url(#jgl2)">J</text>
            </svg>
            <div className="leading-none">
              <p className="font-franklin text-white text-sm tracking-widest">JAIN INFOTECH</p>
              <p className="text-[#0F62FE] text-[10px] tracking-widest font-medium">ADMIN PANEL</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchAll(true)}
              disabled={refreshing}
              data-testid="refresh-btn"
              className="flex items-center gap-1.5 text-xs text-gray-300 hover:text-white border border-gray-600 hover:border-gray-400 px-3 py-1.5 transition-all"
            >
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-1.5 text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 transition-all"
            >
              <LogOut size={13} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="dashboard-error">
            <AlertCircle size={15} /> {error}
            <button onClick={() => setError("")} className="ml-auto text-red-400 hover:text-red-600">✕</button>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="stats-grid">
          <StatCard label="Total Enquiries" value={stats?.total}   icon={Users}    accent="bg-[#0F62FE]" />
          <StatCard label="Today"           value={stats?.today}   icon={Clock}    accent="bg-[#7C3AED]" />
          <StatCard label="This Week"       value={stats?.week}    icon={Calendar} accent="bg-[#D97706]" />
          <StatCard label="This Month"      value={stats?.month}   icon={BarChart2} accent="bg-[#059669]" />
        </div>

        {/* Status Breakdown */}
        {stats?.status_breakdown && (
          <div className="bg-white border border-gray-200 p-4 flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-[#374151] uppercase tracking-wide mr-1">Status breakdown:</span>
            {["New", "In Progress", "Closed"].map(s => (
              <span key={s} className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border ${STATUS_COLORS[s]}`}>
                <CheckCircle2 size={11} />
                {s}: {stats.status_breakdown[s] ?? 0}
              </span>
            ))}
          </div>
        )}

        {/* Filter Bar */}
        <div className="bg-white border border-gray-200 p-4 flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, phone, email, company…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              data-testid="search-input"
              className="w-full pl-8 pr-3 py-2 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={statusFilter}
              onChange={e => setStatus(e.target.value)}
              data-testid="status-filter"
              className="pl-8 pr-7 py-2 border border-gray-300 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#0F62FE] bg-white cursor-pointer"
            >
              <option value="All">All Statuses</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          {/* Source Filter */}
          <div className="relative">
            <select
              value={sourceFilter}
              onChange={e => setSource(e.target.value)}
              data-testid="source-filter"
              className="px-3 pr-7 py-2 border border-gray-300 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#0F62FE] bg-white cursor-pointer"
            >
              <option value="All">All Sources</option>
              <option value="Quote Request">Quote Request</option>
              <option value="Contact Form">Contact Form</option>
              <option value="Live Chat Widget">Live Chat Widget</option>
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          <span className="text-xs text-[#6B7280] ml-auto" data-testid="results-count">
            {totalRows === 0 ? "0 results" : (
              <>Showing <strong>{pageStart}–{pageEnd}</strong> of <strong>{totalRows}</strong></>
            )}
          </span>

          {/* Export PDF */}
          <button
            onClick={exportPDF}
            data-testid="export-pdf-btn"
            className="flex items-center gap-2 bg-[#2D3748] hover:bg-[#1A202C] text-white text-xs font-semibold px-4 py-2 transition-all"
          >
            <Download size={13} />
            Export PDF
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 overflow-hidden" data-testid="enquiries-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#2D3748] text-left">
                  {["#","Name","Company","Phone","Email","Subject","Source","Status","Date","Actions"].map(h => (
                    <th key={h} className="px-3 py-3 text-xs font-semibold text-gray-300 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {enquiries.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-12 text-[#6B7280]">
                      {totalRows === 0 ? "No enquiries yet." : "No enquiries match your filters."}
                    </td>
                  </tr>
                ) : (
                  enquiries.map((enq, idx) => (
                    <tr
                      key={enq.id}
                      className="border-b border-gray-100 hover:bg-[#F9FAFB] transition-colors"
                      data-testid={`enquiry-row-${idx}`}
                    >
                      <td className="px-3 py-3 text-[#9CA3AF] text-xs">{page * PAGE_SIZE + idx + 1}</td>
                      <td className="px-3 py-3 font-semibold text-[#111827] whitespace-nowrap">{enq.name}</td>
                      <td className="px-3 py-3 text-[#4B5563] whitespace-nowrap">{enq.company || "—"}</td>
                      <td className="px-3 py-3 text-[#4B5563] whitespace-nowrap">{enq.phone}</td>
                      <td className="px-3 py-3 text-[#0F62FE] text-xs whitespace-nowrap">
                        <a href={`mailto:${enq.email}`}>{enq.email}</a>
                      </td>
                      <td className="px-3 py-3 text-[#4B5563] max-w-[140px] truncate" title={enq.subject}>
                        {enq.subject || "—"}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {(() => {
                          const b = sourceBadge(enq.source);
                          return (
                            <span
                              className={`text-xs px-2 py-0.5 border font-semibold ${b.cls}`}
                              data-testid={`source-badge-${idx}`}
                            >
                              {b.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-3">
                        <div className="relative">
                          <select
                            value={enq.status || "New"}
                            onChange={e => handleStatusChange(enq, e.target.value)}
                            disabled={updating === enq.id}
                            data-testid={`status-select-${idx}`}
                            className={`text-xs font-semibold px-2 py-1 border appearance-none focus:outline-none cursor-pointer pr-5
                              ${STATUS_COLORS[enq.status || "New"]}
                              ${updating === enq.id ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
                        </div>
                      </td>
                      <td className="px-3 py-3 text-[#9CA3AF] text-xs whitespace-nowrap">
                        {enq.submitted_at
                          ? new Date(enq.submitted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                          : "—"}
                      </td>
                      <td className="px-3 py-3">
                        <button
                          onClick={() => handleDelete(enq.id)}
                          disabled={deleting === enq.id}
                          data-testid={`delete-btn-${idx}`}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 transition-all disabled:opacity-40"
                        >
                          {deleting === enq.id
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Trash2 size={12} />
                          }
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination controls */}
        {totalRows > 0 && (
          <div className="flex items-center justify-between text-xs text-[#4B5563]" data-testid="pagination-controls">
            <span>Page <strong>{page + 1}</strong> of <strong>{totalPages}</strong></span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                data-testid="page-prev-btn"
                className="px-3 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Prev
              </button>
              <button
                type="button"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
                data-testid="page-next-btn"
                className="px-3 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next →
              </button>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 pb-4">
          Jain Infotech Admin Panel — Confidential
        </p>
      </main>
    </div>
  );
}
